(ns nosco.ui.popup
  (:require
   ["focus-layers" :default useFocusLock]
   ["react" :as React]
   ["react-popper" :as popper]
   ["react-useportal" :default usePortal]
   [goog.object]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.hooks :as nosco-hooks]
   [nosco.polyfills]
   [nosco.react]))

(def ref-width-modifier
  "A custom modifier for Popper that sets the popper element
  width equal to the reference element width."
  {:name "ref-width-modifier"
   :phase "beforeWrite"
   :enabled true
   :requires ["computeStyles"]
   :fn (fn [modifier-args]
         (set! (.. ^js modifier-args -state -styles -popper -width)
               (.. ^js modifier-args -state -rects -reference -width))
         nil)})

(defn popper-options
  [{:keys [placement flip arrow-element prevent-overflow offset use-ref-width? strategy]
    :or {strategy "absolute"}}]
  (clj->js
   {:placement placement
    :strategy strategy
    :modifiers (keep
                identity
                [(cond-> {:name "flip" :enabled (boolean flip)}
                   (map? flip)
                   (assoc :options flip))
                 (when prevent-overflow
                   {:name "preventOverflow" :options prevent-overflow})
                 (when offset
                   {:name "offset" :options {:offset offset}})
                 (when arrow-element
                   {:name "arrow" :options {:element arrow-element}})
                 (when use-ref-width?
                   ref-width-modifier)])}))

(defn popper-for-overlay [popper popper-ref-cb]
  {:popper-ref-cb popper-ref-cb
   :popper popper})

(def use-popper-native popper/usePopper)

(defn use-popper
  "Returns a vector of a ref callback to be passed to the reference element, a map to be passed as the :popper prop to `Overlay`
  and the popper update function used to manually update the popper instance (typically used when switching visibility)

   See `popper-options` for the accepted options."
  [opts]
  (let [[reference-el ->reference-el] (hooks/useState nil)
        [popper-el ->popper-el] (hooks/useState nil)
        popper (popper/usePopper
                reference-el
                popper-el
                (popper-options opts))]
    [->reference-el (popper-for-overlay popper ->popper-el) (.-update popper)]))

(defn popper-initialized?
  "Returns true when popper has initialized the position of the overlay."
  [popper]
  (some?
   (some-> popper :popper (.-styles) (.-popper) (.-transform))))

(defn- ref? [x]
  (and (= "object" (goog/typeOf x))
       (goog.object/containsKey x "current")))

(defn- ->node [x]
  (if (ref? x)
    (.-current x)
    x))

(defn- some-contains? [refs el]
  (some (fn [node]
          (or (= node el)
              (.contains node el)))
        (keep ->node refs)))

(defn- use-mouse-event-outside-impl [event-type open f refs]
  (nosco-hooks/maybe-use-event-listener
   open
   ;; attaching to document.body means this is listened to from all
   ;; places, including Portals etc
   js/document.body
   event-type
   (fn [event]
     (when (and (not (some-contains? refs (.-target event)))
                (= 0 (.-button event))
                (= false
                   (.-altKey event)
                   (.-ctrlKey event)
                   (.-shiftKey event)
                   (.-metaKey event)))
       (f event)))

   refs
   ;; moving this to the capture phase so that it runs before
   ;; React event handlers run https://reactjs.org/blog/2020/08/10/react-v17-rc.html#fixing-potential-issues
   #js {:capture true}))

(defn use-click-outside
  "When open is true, calls f when any non-modified left click occurs outside of
  the elements in refs."
  [open f & refs]
  (use-mouse-event-outside-impl "click" open f refs))

(defn use-mousedown-outside
  "When open is true, calls f when any non-modified left click occurs outside of
  the elements in refs."
  [open f & refs]
  (use-mouse-event-outside-impl "mousedown" open f refs))

(defn use-escape
  "When open is true, calls f when escape is pressed."
  [open f & refs]
  (nosco.hooks/maybe-use-event-listener
   open
   js/window
   "keydown"
   (fn [event]
     (when (= "Escape" (.-key event))
       (f event)))
   refs
   ;; moving this to the capture phase so that it runs before
   ;; React event handlers run https://reactjs.org/blog/2020/08/10/react-v17-rc.html#fixing-potential-issues
   #js {:capture true}))

(defn use-tab
  "When open is true, calls f when tab is pressed."
  [open f]
  (nosco.hooks/maybe-use-event-listener
   open
   js/window
   "keydown"
   (fn [event]
     (when (= "Tab" (.-key event))
       (f event)))))

;; Render a separate component with the children so that useFocusLock is only
;; called when the overlay is open, and so that we can provide the
;; overlay container context

(def OverlayContainerContext
  "If you're rendering a popup inside a scrollable element, create a new provider
   with the DOM node of the scrollable element. This way the popup gets attached
   inside the scrollable element and scrolls with it, avoiding a jumpy/janky behaviour
   when popper.js repositions it on every tick."
  (React/createContext nil))

(defnc ^:private Overlay* [{:keys [lock-focus position-initialized? children]}]
  (let [ref (React/useRef)
        overlay-container-ref (React/useRef)]
    (when (and lock-focus (not (nosco.polyfills/IE11?)))
      (useFocusLock ref))
    [:div {:ref (when position-initialized? ref)}
     [:div {:ref overlay-container-ref}
      [:provider {:context OverlayContainerContext
                  :value (.-current overlay-container-ref)}
       children]]]))

(defnc Overlay
  "Props:
  - :popper - Returned by `use-popper`
  - :open - Whether the popup is open
  - :lock-focus - When true, lock focus within the overlay when it's open"
  [{:keys [popper open lock-focus children class]} ref]
  ;; since the popper is immediately attached, we have to do the layout calculations
  ;; when the overlay is open
  (hooks/useLayoutEffect
   (fn []
     (when open
       (when-let [popper-js (:popper popper)]
         ;; as per the docs, update can be null when popper is destroyed
         ;; or not yet initialized, so guard it
         (when (.-update ^js popper-js)
           ;; update instead of forceUpdate because we get an error
           ;; in the console about calling "React.flushSync" during
           ;; a lifecycle method
           (.update ^js popper-js))))
     js/undefined)
   [open])
  (let [container-ref (hooks/useContext OverlayContainerContext)]
    [(.-Portal (usePortal #js {:bindTo container-ref}))
     [:div {:ref ref}
      [:div
       (merge
         {:ref (:popper-ref-cb popper)
          :class ["nosco-overlay2" class]
          :style (.-popper (.-styles (:popper popper)))}
         (js->clj (.-popper (.-attributes (:popper popper))) :keywordize-keys true))
       (when open
         [Overlay*
          {:lock-focus lock-focus
           ;; Move focus to the overlay only when its position has been initialized
           :position-initialized? (popper-initialized? popper)}
          children])]]]))

(nosco.react/forward-ref! Overlay)
